import React from 'react';
import { Button } from 'rsuite';
import ProfilePhoto from '../employees/ProfilePhoto';
import {
  RECEIPT_FREE_CATEGORIES,
  getCategoryLabel,
  normalizeCategory,
} from '../../utils/expenseCategories';
import { employeeName, formatAge, formatCurrency, formatDate } from './approvalFormat';
import './ApprovalCards.css';

// Auto-approved ("Entered") time off carries this sentinel in approval_reason
// instead of a real comment, so it must never be shown as one.
const NO_APPROVAL_REQUIRED_REASON = 'no approval required';

// The stored status and the word for it differ: 'draft' is saved but never sent,
// 'pending' has been sent and is waiting. Same two labels the employee's own
// lists have always used, so a card doesn't rename a state by moving page.
const STATUS_LABELS = {
  draft: 'Not submitted',
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

const TIME_OFF_TYPES = {
  vacation: { icon: 'bi-airplane-fill', label: 'Vacation' },
  sick: { icon: 'bi-heart-pulse-fill', label: 'Sick' },
  reduction: { icon: 'bi-clock-fill', label: 'Reduction' },
};

const timeOffType = (value) => {
  const key = String(value || '').trim().toLowerCase();
  return TIME_OFF_TYPES[key]
    || { icon: 'bi-calendar-event', label: key ? key[0].toUpperCase() + key.slice(1) : 'Time Off' };
};

const formatTime = (minutes) => {
  const total = Number(minutes);
  if (!Number.isFinite(total)) return '';
  const hour24 = Math.floor(total / 60) % 24;
  const mins = total % 60;
  const suffix = hour24 >= 12 ? 'pm' : 'am';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return mins === 0 ? `${hour12}${suffix}` : `${hour12}:${String(mins).padStart(2, '0')}${suffix}`;
};

const Cell = ({ label, children, wide = false }) => (
  <div className={`approval-card__cell${wide ? ' approval-card__cell--wide' : ''}`}>
    <div className="approval-card__cell-label">{label}</div>
    <div className="approval-card__cell-value">{children}</div>
  </div>
);

const Chip = ({ icon, tone, children }) => (
  <span className={`approval-card__chip${tone ? ` approval-card__chip--${tone}` : ''}`}>
    {icon ? <i className={`bi ${icon}`} aria-hidden="true" /> : null}
    {children}
  </span>
);

/**
 * One request, expense or time off, wherever it is being looked at.
 *
 * Both kinds share a shape — who, four facts, what they said, then the actions —
 * so a mixed queue reads at a glance and the eye lands in the same place on
 * every card. The facts themselves differ by kind, which is the only place the
 * two diverge. The same card serves the approver deciding it and the employee
 * who filed it; what changes between them is the buttons along the bottom, which
 * is why they are passed in rather than baked in.
 *
 * - `clearing` covers the moment between a decision landing and the record
 *   leaving the query data, so the card can animate out rather than blink away.
 * - `showStatus` puts the status on the card. The approvals queue leaves it off
 *   for pending records — everything there is pending — but a list holding
 *   drafts and decisions alike has to say which each one is.
 * - `attribution` names whoever filed it for someone else.
 * - `actions` are extra buttons beside the primary one: withdraw, delete, edit.
 */
export default function ApprovalRequestCard({
  kind,
  record,
  onReview,
  clearing = false,
  showStatus = false,
  showApprovalDetails = false,
  attribution = '',
  primaryLabel,
  actions = [],
}) {
  if (!record) return null;

  const isExpense = kind === 'expense';
  const status = String(record.status || '').toLowerCase();
  // A decided record is only here because it was asked for; there is nothing to
  // review on it, so the card offers to show it instead.
  const isPending = status === 'pending';
  const name = employeeName(record);
  const age = formatAge(record.created_at);
  const type = isExpense ? null : timeOffType(record.request_type);
  const notes = isExpense ? record.description : record.notes;
  const attachments = Number(record.attachment_count || 0);
  // Technology reimbursements and pre-approved meals are paid without a
  // receipt, so an empty attachment list is expected for them, not flagged.
  const receiptExpected = isExpense
    && !RECEIPT_FREE_CATEGORIES.has(normalizeCategory(record.category));
  const startTime = record?.start_minutes == null ? '' : formatTime(record.start_minutes);
  const endTime = record?.end_minutes == null ? '' : formatTime(record.end_minutes);
  const isClickable = typeof onReview === 'function';

  const comment = String(record.approval_reason || '').trim();
  const isNoApprovalRequired = comment.toLowerCase() === NO_APPROVAL_REQUIRED_REASON;
  const showComment = comment && !isNoApprovalRequired;

  // Time off that never needed an approver was entered, not approved, and has
  // always been labelled that way on the employee's own list.
  const statusLabel = (!isExpense && status === 'approved' && isNoApprovalRequired)
    ? 'Entered'
    : (STATUS_LABELS[status] || record.status);

  const approverName = `${record.approver_first_name || ''} ${record.approver_last_name || ''}`.trim()
    || record.approver_email
    || record.approved_by;
  const showApprover = showApprovalDetails && status === 'approved' && approverName;

  return (
    <article
      className={[
        'approval-card',
        // A decided card is edged in the colour of its decision, so a rejection
        // is visible from across the grid rather than only in the badge.
        status === 'approved' || status === 'rejected' ? `approval-card--${status}` : '',
        clearing ? 'approval-card--clearing' : '',
        isClickable ? '' : 'approval-card--static',
      ].filter(Boolean).join(' ')}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? () => onReview(record) : undefined}
      onKeyDown={isClickable ? (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onReview(record);
      } : undefined}
    >
      <header className="approval-card__head">
        <ProfilePhoto
          employeeId={record.employee_id}
          size="small"
          firstName={record.first_name || record.employee_first_name}
          lastName={record.last_name || record.employee_last_name}
        />
        <div className="approval-card__who">
          <div className="approval-card__name" title={name}>{name}</div>
          <div className="approval-card__age">
            {status === 'draft' ? 'Not submitted' : `Submitted ${age}`}
          </div>
        </div>
        {/* Amount and status stack in the corner: both are read at a glance and
            neither should push the name out of the card. */}
        <div className="approval-card__marks">
          <span className={`approval-card__kind approval-card__kind--${isExpense ? 'expense' : 'timeoff'}`}>
            {isExpense ? formatCurrency(record.amount) : `${Number(record.total_hours ?? 0)} hrs`}
          </span>
          {showStatus || (status && !isPending) ? (
            <span className={`approval-card__status approval-card__status--${status}`}>
              {statusLabel}
            </span>
          ) : null}
        </div>
      </header>

      <div className="approval-card__grid">
        {isExpense ? (
          <>
            <Cell label="Purchase Date">{formatDate(record.purchase_date) || '—'}</Cell>
            <Cell label="Category">{getCategoryLabel(record.category) || '—'}</Cell>
            <Cell label="Vendor">{record.vendor_name || '—'}</Cell>
            <Cell label="Attachments">
              <i className="bi bi-paperclip" aria-hidden="true" /> {attachments}
            </Cell>
          </>
        ) : (
          <>
            <Cell label={`Start${startTime ? ` @ ${startTime}` : ''}`}>
              {formatDate(record.start_date) || '—'}
            </Cell>
            <Cell label={`End${endTime ? ` @ ${endTime}` : ''}`}>
              {formatDate(record.end_date) || '—'}
            </Cell>
            {/* Spans both columns: the hours and the kind of leave are the two
                facts a decision actually turns on. */}
            <Cell label="Type" wide>
              <i className={`bi ${type.icon}`} aria-hidden="true" /> {type.label}
            </Cell>
          </>
        )}
      </div>

      {/* Only the flags that change a decision, so the strip stays empty on an
          ordinary request and loud on one that needs a second look. */}
      <div className="approval-card__chips">
        {attribution ? <Chip icon="bi-person-fill-up" tone="accent">{attribution}</Chip> : null}
        {record.group_name ? <Chip icon="bi-collection">{record.group_name}</Chip> : null}
        {record.customer_reimbursable ? (
          <Chip icon="bi-arrow-repeat" tone="billable">
            {record.customer_name || 'No customer set'}
          </Chip>
        ) : null}
        {record.purchased_by_me ? <Chip icon="bi-credit-card-2-front" tone="card">Company card</Chip> : null}
        {record.travel ? <Chip icon="bi-airplane">Travel</Chip> : null}
        {receiptExpected && attachments === 0 ? (
          <Chip icon="bi-exclamation-triangle" tone="warn">No receipt</Chip>
        ) : null}
      </div>

      {notes ? (
        <p className="approval-card__notes">
          <span className="approval-card__field-label">{isExpense ? 'Description' : 'Notes'}</span>
          {notes}
        </p>
      ) : null}

      {/* Shown in full rather than as a hover-only badge: a comment left on a
          pending request is usually a question waiting on an answer, and it is
          worth reading before opening anything. Once decided, the same field is
          the reason for the decision. */}
      {showComment ? (
        <p className="approval-card__comment">
          <span className="approval-card__field-label">
            <i className="bi bi-chat-left-text" aria-hidden="true" />
            {isPending ? ' Comment' : ' Decision'}
          </span>
          {comment}
        </p>
      ) : null}

      {showApprover ? (
        <p className="approval-card__comment">
          <span className="approval-card__field-label">
            <i className="bi bi-check2-circle" aria-hidden="true" /> Approved by
          </span>
          {approverName}
          {record.approved_at ? ` · ${formatDate(record.approved_at)}` : ''}
        </p>
      ) : null}

      {/* Deliberately a div, not a <footer>: App.css pins every footer element
          to the bottom of the viewport with position: fixed. */}
      {isClickable || actions.length > 0 ? (
        <div className="approval-card__foot">
          {isClickable ? (
            <Button
              appearance={isPending ? 'primary' : 'ghost'}
              block={actions.length === 0}
              className={actions.length > 0 ? 'approval-card__foot-primary' : undefined}
              onClick={(event) => {
                event.stopPropagation();
                onReview(record);
              }}
            >
              {primaryLabel || (isPending ? 'Review' : 'View')}
            </Button>
          ) : null}

          {actions.map((action) => (
            <Button
              key={action.key || action.label}
              appearance={action.appearance || 'ghost'}
              color={action.color}
              block={!isClickable && actions.length === 1}
              onClick={(event) => {
                event.stopPropagation();
                action.onClick?.(record);
              }}
            >
              {action.label}
            </Button>
          ))}
        </div>
      ) : null}
    </article>
  );
}
